import {ChevronDownIcon, ChevronRightIcon, CommentIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Banner, Button, SegmentedControl} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  type AuthorFilterItem,
  CommentsFilter,
  type CommentsFilterState,
  getDefaultFilterState,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './CommentsFilter'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PageLimits} from '../../page-data/payloads/files'
import {ZeroState} from './ZeroState'
import {useInfiniteThreadPreviewsPageData} from '../../page-data/loaders/use-thread-previews'
import {useInfiniteIssueCommentsPageData} from '../../page-data/loaders/use-issue-comments'
import {useInfiniteReviewSummariesPageData} from '../../page-data/loaders/use-review-summaries'
import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueCommentItem} from './IssueCommentItem'
import {ReviewSummaryItem} from './ReviewSummaryItem'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {
  useThreads,
  useIssueComments,
  useReviewSummaries,
  useCombinedCurrentUser,
  useDiffPathOrderMap,
} from '../../stores/selectors'
import {
  type ConversationItem,
  reviewSummaryToIssueComment,
  buildConversationItems,
} from './review-summary-to-issue-comment'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {getItem, setItem} from '@github-ui/safe-storage/session-storage'
import {IDS} from '@github-ui/commenting/DomElements'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {DockedPanel, useIsDockedPanelCompact, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {createPortal} from 'react-dom'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import type {AliveChannels} from '../../page-data/payloads/header'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'
import {noop} from '@github-ui/noop'
import styles from './CommentsSidePanel.module.css'
import {useCompositeThreadPositioning} from '../../hooks/use-composite-thread-positioning'
import {clsx} from 'clsx'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits: PageLimits
  pathName: string
  pullRequestAuthorLogin: string
  aliveChannels: AliveChannels
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
}

interface CommentPanelHeaderContentProps {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  threadPreviewsAuthorList: AuthorFilterItem[]
  issueCommentsAuthorList: AuthorFilterItem[]
  threadPreviewFilterState: CommentsFilterState
  setThreadPreviewFilterState: (state: CommentsFilterState) => void
  issueCommentsFilterState: CommentsFilterState
  setIssueCommentsFilterState: (state: CommentsFilterState) => void
  iconOnlyReviewCommentsFilter?: boolean
}
/**
 * Renders the header content for the comments side panel, including a segmented control
 * for toggling between review (diff) comments and conversation (issue) comments views,
 * along with the corresponding filter controls for each view mode.
 *
 * Each view mode has its own independent filter state and author list.
 *
 * @param viewMode - The currently active view mode (`'thread-previews'` or `'issue-comments'`).
 * @param setViewMode - Callback to update the active view mode.
 * @param threadPreviewsAuthorList - List of authors available for filtering in the review comments view.
 * @param issueCommentsAuthorList - List of authors available for filtering in the conversation comments view.
 * @param threadPreviewFilterState - The current filter state for the review comments view.
 * @param setThreadPreviewFilterState - Callback to update the filter state for the review comments view.
 * @param issueCommentsFilterState - The current filter state for the conversation comments view.
 * @param setIssueCommentsFilterState - Callback to update the filter state for the conversation comments view.
 * @param iconOnlyReviewCommentsFilter - If true, the filter button for review comments will be minimized to an icon button without text.
 */
function CommentPanelHeaderContent({
  viewMode,
  setViewMode,
  threadPreviewsAuthorList,
  issueCommentsAuthorList,
  threadPreviewFilterState,
  setThreadPreviewFilterState,
  issueCommentsFilterState,
  setIssueCommentsFilterState,
  iconOnlyReviewCommentsFilter,
}: CommentPanelHeaderContentProps) {
  return (
    <div className="d-flex flex-row flex-items-center gap-2 py-2">
      <SegmentedControl
        aria-label="Comment view mode"
        onChange={(value: number) => {
          setViewMode(value === 0 ? 'thread-previews' : 'issue-comments')
        }}
      >
        <SegmentedControl.Button selected={viewMode === 'thread-previews'}>Review</SegmentedControl.Button>
        <SegmentedControl.Button selected={viewMode === 'issue-comments'}>Conversation</SegmentedControl.Button>
      </SegmentedControl>
      {viewMode === 'thread-previews' && (
        <CommentsFilter
          authorList={threadPreviewsAuthorList}
          className="width-full"
          filterState={threadPreviewFilterState}
          onFilterStateChange={setThreadPreviewFilterState}
          hideReviewThreadFilters={false}
          iconOnlyReviewCommentsFilter={iconOnlyReviewCommentsFilter}
        />
      )}
      {viewMode === 'issue-comments' && (
        <CommentsFilter
          authorList={issueCommentsAuthorList}
          className="width-full"
          filterState={issueCommentsFilterState}
          onFilterStateChange={setIssueCommentsFilterState}
          hideReviewThreadFilters
        />
      )}
    </div>
  )
}

interface CommentsPanelBodyProps {
  filteredIssueCommentIds: Set<string>
  filteredThreadIds: Set<string>
  handleQuoteReply: (quotedText: string) => void
  isOpen: boolean
  conversationItems: ConversationItem[]
  limitCount: number
  limitExceeded: boolean
  onClose: () => void // Remove onClose callback when deprecating non-docked panel
  pathName: string
  pullRequestAuthorLogin: string
  threadPreviews: Thread[]
  tabSize?: number
  totalDiffCommentCount: number
  viewMode: ViewMode
}

function CommentsErrorFallback() {
  return (
    <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
      <ZeroState
        description="Try reloading the pull request page."
        heading="Unable to load comments"
        icon={CommentIcon}
        headingLevel="h2"
      />
    </div>
  )
}

/**
 * Renders the body content of the Comments Side Panel.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded via `Suspense` with a skeleton fallback.
 *
 * @param filteredThreadIds - The set of thread IDs to display after filtering has been applied.
 * @param isOpen - Whether the comments panel is currently open.
 * @param limitCount - The maximum number of latest comments shown when the limit is exceeded.
 * @param limitExceeded - Whether the number of comments exceeds the displayable limit.
 * @param onClose - Callback invoked when the panel is closed.
 * @param pathName - The current file path name used to scope displayed comments.
 * @param pullRequestAuthorLogin - The login handle of the pull request author, used for issue comments rendering.
 * @param tabSize - Optional tab size for rendering code snippets.
 * @param threadPreviews - The current set of thread previews to render.
 * @param viewMode - The current view mode, either `'thread-previews'` or another value for issue comments.
 */
function CommentsPanelBody({
  filteredIssueCommentIds,
  filteredThreadIds,
  handleQuoteReply,
  isOpen,
  conversationItems,
  limitCount,
  limitExceeded,
  onClose, // Remove onClose callback when deprecating non-docked panel
  pathName,
  pullRequestAuthorLogin,
  tabSize,
  threadPreviews,
  totalDiffCommentCount,
  viewMode,
}: CommentsPanelBodyProps) {
  return (
    <>
      {viewMode === 'thread-previews' && totalDiffCommentCount > 0 && (
        <span style={{marginTop: '-4px'}} className="mb-2 fgColor-muted text-small text-center d-block">
          Showing {filteredThreadIds.size} of {totalDiffCommentCount} comments
        </span>
      )}
      {viewMode === 'thread-previews' && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="tmp-mb-3"
        />
      )}
      <ErrorBoundary boundaryName="CommentsSidePanelContent" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          {viewMode === 'thread-previews' ? (
            <ThreadPreviewsContent
              filteredThreadIds={filteredThreadIds}
              isOpen={isOpen}
              pathName={pathName}
              tabSize={tabSize}
              onClose={onClose} // Remove onClose callback when deprecating non-docked panel
              threadPreviews={threadPreviews}
            />
          ) : (
            <IssueCommentsContent
              filteredThreadIds={filteredIssueCommentIds}
              isOpen={isOpen}
              conversationItems={conversationItems}
              pathName={pathName}
              pullRequestAuthorLogin={pullRequestAuthorLogin}
              onQuoteReply={handleQuoteReply}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  )
}

const collapseStateLocalStorageKey = 'pullRequestCommentsPanelFooterCollapseState'

interface CommentsSidePanelFooterHandle {
  expand: () => boolean
}

interface CommentsSidePanelFooterProps {
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmitComment: () => void
  isPending: boolean
  ref?: React.Ref<CommentsSidePanelFooterHandle>
}

function CommentsSidePanelFooter({
  commentText,
  onCommentTextChange,
  onSubmitComment,
  isPending,
  ref,
}: CommentsSidePanelFooterProps) {
  // TODO: if this becomes pinnable, we will have to come up with an SSR safe strategy that
  // avoids CLS. Might have to be a setting we store on the server and return in the payload
  const defaultCollapseState = getItem(collapseStateLocalStorageKey)
  const [isMinimized, setIsMinimized] = useState(defaultCollapseState === 'true')

  useImperativeHandle(
    ref,
    () => ({
      expand() {
        const wasMinimized = isMinimized
        setIsMinimized(false)
        setItem(collapseStateLocalStorageKey, 'false')
        return wasMinimized
      },
    }),
    [isMinimized],
  )

  return (
    <div className={clsx(styles.SidePanelFooter, 'd-flex flex-column gap-2 width-full')} id={IDS.issueCommentComposer}>
      <h4 className="d-flex">
        <IconButton
          aria-label={isMinimized ? 'Open PR comment panel' : 'Close PR comment panel'}
          icon={isMinimized ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          aria-expanded={!isMinimized}
          tooltipDirection="n"
          variant="invisible"
          className="mr-2"
          onClick={() => {
            const newState = !isMinimized
            setIsMinimized(newState)
            setItem(collapseStateLocalStorageKey, String(newState))
          }}
        />
        <span className="pb-2">Add a comment</span>
      </h4>
      {!isMinimized && (
        <ConversationCommentBox
          label="Add a comment"
          value={commentText}
          onChange={onCommentTextChange}
          onPrimaryAction={onSubmitComment}
          disabled={isPending}
          maxHeightLines={15}
        >
          <Button variant="primary" onClick={onSubmitComment} inactive={isPending || commentText.trim().length === 0}>
            Comment
          </Button>
        </ConversationCommentBox>
      )}
    </div>
  )
}

type ThreadPreviewsContentProps = Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'onClose' | 'pathName' | 'isOpen' | 'tabSize'
> & {
  threadPreviews: Thread[]
}

/**
 * Content component that fetches and renders thread previews.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function ThreadPreviewsContentWithDataLoading({
  filteredThreadIds,
  onClose, // Remove onClose callback when deprecating non-docked panel
  pathName,
  isOpen,
  tabSize,
  threadPreviews,
}: ThreadPreviewsContentProps) {
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingThreadPreviews,
    fetchNextPage,
  } = useInfiniteThreadPreviewsPageData({
    pathName,
  })

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    let cancelled = false
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingThreadPreviews && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        if (!cancelled) fetchingRef.current = false
      }
    }
    void fetchAllPages()
    return () => {
      cancelled = true
    }
  }, [hasNextPage, isFetchingThreadPreviews, fetchNextPage])

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const handleNavigateToDiffComment = useCallback(() => {
    onClose() // Remove onClose callback when deprecating non-docked panel
  }, [onClose])

  const threadSummaries = threadPreviews
    .map(thread => {
      if (!thread || !filteredThreadIds.has(thread.id)) return null
      return (
        <ThreadPreview
          key={thread.id}
          tabSize={tabSize}
          thread={thread}
          onNavigateToDiffComment={handleNavigateToDiffComment}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  // Show loading state when actively fetching OR when there's a next page about to be fetched
  // (the useEffect will trigger fetchNextPage, but isFetchingNextPage won't be true until after the render)
  const isLoadingMore = isFetchingThreadPreviews || hasNextPage

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isLoadingMore) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isLoadingMore, debouncedAnnounce, threadSummaries])

  return (
    <>
      {threadSummaries.length > 0 ? (
        <ErrorBoundary boundaryName="ThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            <div className="d-flex flex-column position-relative width-full gap-3">
              {threadSummaries}
              {isLoadingMore && <ThreadPreviewSkeleton count={1} />}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : isLoadingMore ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

/**
 * Content component that renders thread previews using data from the store.
 * Data is expected to be loaded from diff lines store.
 * Fetches positioning data for threads outside the current diff.
 */
function ThreadPreviewsContentUsingStore({
  filteredThreadIds,
  onClose, // Remove onClose callback when deprecating non-docked panel
  isOpen,
  tabSize,
  threadPreviews,
}: ThreadPreviewsContentProps) {
  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const handleNavigateToDiffComment = useCallback(() => {
    onClose() // Remove onClose callback when deprecating non-docked panel
  }, [onClose])

  // Handle positioning data for threads inside and outside the current diff
  useCompositeThreadPositioning(threadPreviews)

  const threadSummaries = threadPreviews
    .map(thread => {
      if (!thread || !filteredThreadIds.has(thread.id)) return null
      return (
        <ThreadPreview
          key={thread.id}
          tabSize={tabSize}
          thread={thread}
          onNavigateToDiffComment={handleNavigateToDiffComment}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  useEffect(() => {
    if (!isOpen) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, debouncedAnnounce, threadSummaries])

  return (
    <>
      {threadSummaries.length > 0 ? (
        <ErrorBoundary boundaryName="DockedThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            <div className="d-flex flex-column position-relative width-full gap-3">{threadSummaries}</div>
          </Suspense>
        </ErrorBoundary>
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

/**
 * Content component wrapper that conditionally uses data loading based on feature flag.
 */
function ThreadPreviewsContent(props: ThreadPreviewsContentProps) {
  const removeThreadPreviewsLoad = isFeatureEnabled('load_paginated_threads_on_diff')

  if (removeThreadPreviewsLoad) {
    return <ThreadPreviewsContentUsingStore {...props} />
  }

  return <ThreadPreviewsContentWithDataLoading {...props} />
}

interface IssueCommentsContentProps extends Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'pathName' | 'isOpen' | 'pullRequestAuthorLogin'
> {
  conversationItems: ConversationItem[]
  onQuoteReply: (quotedText: string) => void
}

/**
 * Content component that fetches and renders issue comments and review summaries.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function IssueCommentsContent({
  filteredThreadIds,
  pathName,
  isOpen,
  pullRequestAuthorLogin,
  conversationItems,
  onQuoteReply,
}: IssueCommentsContentProps) {
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingIssueComments,
    fetchNextPage,
  } = useInfiniteIssueCommentsPageData({
    pathName,
  })

  // Fetch review summaries in parallel with issue comments
  const {
    hasNextPage: hasNextReviewSummariesPage,
    isFetchingNextPage: isFetchingReviewSummaries,
    fetchNextPage: fetchNextReviewSummariesPage,
  } = useInfiniteReviewSummariesPageData({
    pathName,
  })

  const hasConversationItems = conversationItems.length > 0

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)
  const reviewSummariesFetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    let cancelled = false
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingIssueComments && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        if (!cancelled) fetchingRef.current = false
      }
    }
    void fetchAllPages()
    return () => {
      cancelled = true
    }
  }, [hasNextPage, isFetchingIssueComments, fetchNextPage])

  // Automatically fetch next review summaries page when available
  useEffect(() => {
    let cancelled = false
    const fetchAllReviewSummariesPages = async () => {
      if (hasNextReviewSummariesPage && !isFetchingReviewSummaries && !reviewSummariesFetchingRef.current) {
        reviewSummariesFetchingRef.current = true
        await fetchNextReviewSummariesPage()
        if (!cancelled) reviewSummariesFetchingRef.current = false
      }
    }
    void fetchAllReviewSummariesPages()
    return () => {
      cancelled = true
    }
  }, [hasNextReviewSummariesPage, isFetchingReviewSummaries, fetchNextReviewSummariesPage])

  const visibleComments = useMemo(
    () => conversationItems.filter(item => filteredThreadIds.has(item.data.id)),
    [conversationItems, filteredThreadIds],
  )

  // Show loading state when either source is actively fetching or has more pages
  const isLoadingMore =
    isFetchingIssueComments || hasNextPage || isFetchingReviewSummaries || hasNextReviewSummariesPage

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isLoadingMore) return

    const announceText =
      visibleComments.length > 0
        ? `${visibleComments.length} ${visibleComments.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isLoadingMore, debouncedAnnounce, visibleComments, hasConversationItems])
  const canReact = isLoggedIn()

  return (
    <>
      {visibleComments.length > 0 ? (
        <ErrorBoundary boundaryName="IssueCommentsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            <div className="d-flex flex-column position-relative width-full gap-3">
              {visibleComments.map(item =>
                item.type === 'review-summary' ? (
                  <ReviewSummaryItem
                    key={`review-${item.data.id}`}
                    reviewSummary={item.data}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                  />
                ) : (
                  <IssueCommentItem
                    key={`comment-${item.data.id}`}
                    comment={item.data}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                  />
                ),
              )}
              {isLoadingMore && <ThreadPreviewSkeleton count={1} />}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : isLoadingMore ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasConversationItems ? 'No comments match the current filter' : 'No comments yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

type ViewMode = 'thread-previews' | 'issue-comments'

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement | null>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {pathName, toggleSidesheetRef, pageLimits, pullRequestAuthorLogin, aliveChannels, isOpen, onClose, tabSize} =
    props

  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  // Get portal target - only accessed when panel is open, so SSR-safe
  const sidePanelContent = isDockedPanelOpen ? ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID) : null

  const [viewMode, setViewMode] = useState<ViewMode>('thread-previews')
  const unsortedThreadPreviews = useThreads()
  const totalDiffCommentCount = unsortedThreadPreviews.length
  const diffPathOrderMap = useDiffPathOrderMap()
  const threadPreviews = useMemo(() => {
    if (diffPathOrderMap.size === 0) return unsortedThreadPreviews
    return [...unsortedThreadPreviews].sort((a, b) => {
      const pathA = a.path ?? a.positioning?.path
      const pathB = b.path ?? b.positioning?.path
      const orderA = pathA !== undefined ? diffPathOrderMap.get(pathA) : undefined
      const orderB = pathB !== undefined ? diffPathOrderMap.get(pathB) : undefined
      // Threads without a matching diff entry sort to the end
      if (orderA === undefined && orderB === undefined) return 0
      if (orderA === undefined) return 1
      if (orderB === undefined) return -1
      if (orderA !== orderB) return orderA - orderB
      // Same file: sort by line number
      const lineA = a.subjectType === 'FILE' ? 0 : (a.positioning?.endLine ?? a.line ?? a.subject?.endLine)
      const lineB = b.subjectType === 'FILE' ? 0 : (b.positioning?.endLine ?? b.line ?? b.subject?.endLine)
      return (lineA ?? Infinity) - (lineB ?? Infinity)
    })
  }, [unsortedThreadPreviews, diffPathOrderMap])
  const issueComments = useIssueComments()
  const reviewSummaries = useReviewSummaries()
  const currentUser = useCombinedCurrentUser()

  // Build a chronologically sorted list of conversation items (issue comments + review summaries)
  // preserving original types so each renders with its dedicated component
  const conversationItems = useMemo(
    () => buildConversationItems(issueComments, reviewSummaries),
    [issueComments, reviewSummaries],
  )

  // Flatten to IssueComment shape for filtering (useFilterIssueCommentsIds expects IssueComment[])
  const mergedConversationComments = useMemo(() => {
    const convertedSummaries = reviewSummaries.map(reviewSummaryToIssueComment)
    return [...issueComments, ...convertedSummaries]
  }, [issueComments, reviewSummaries])

  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredIssueCommentIds = useFilterIssueCommentsIds(mergedConversationComments, issueCommentsFilterState)

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<CommentsSidePanelFooterHandle>(null)

  // State for comment footer
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending: isSubmittingComment} = useCreatePullRequestTimelineCommentMutation()

  const handleCommentTextChange = useCallback((value: string) => {
    setCommentText(value)
  }, [])

  const handleSubmitComment = useCallback(() => {
    if (commentText.trim().length === 0 || isSubmittingComment) return
    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          setCommentText('')
          requestAnimationFrame(() => {
            ssrSafeDocument
              ?.getElementById(`issue-comment-${data.issueComment.id}`)
              ?.scrollIntoView({behavior: 'smooth'})
          })
        },
      },
    )
  }, [commentText, createTimelineComment, isSubmittingComment])

  const handleQuoteReply = useCallback((quotedText: string) => {
    const wasMinimized = footerRef.current?.expand() ?? false
    if (wasMinimized) {
      setCommentText(prev => {
        if (prev.trim().length > 0) {
          return `${prev}\n\n${quotedText}`
        }
        return quotedText
      })
    } else {
      setCommentText(quotedText)
    }
  }, [])

  const threadPreviewsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const threadPreview of threadPreviews) {
      const firstComment = getFirstComment(threadPreview)
      if (firstComment?.author && !authors.some(author => author.login === firstComment.author?.login)) {
        // Copilot goes to the top of the filter
        if (firstComment.author.login === 'Copilot') {
          authors.unshift(firstComment.author)
        } else {
          authors.push(firstComment.author)
        }
      }
    }

    return authors
  }, [threadPreviews])

  const issueCommentsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const comment of mergedConversationComments) {
      if (comment.authorLogin && !authors.some(author => author.login === comment.authorLogin)) {
        // Copilot goes to the top of the filter
        if (comment.authorLogin === 'Copilot') {
          authors.unshift({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        } else {
          authors.push({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        }
      }
    }

    return authors
  }, [mergedConversationComments])

  const isCommentPanelFooterEnabled = viewMode === 'issue-comments' && currentUser.canComment
  const commentPanelFooter = isCommentPanelFooterEnabled ? (
    <Dialog.Footer>
      <CommentsSidePanelFooter
        ref={footerRef}
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        onSubmitComment={handleSubmitComment}
        isPending={isSubmittingComment}
      />
    </Dialog.Footer>
  ) : null

  const limitExceeded = pageLimits.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits.reviewThreadsPaginatedLimit

  // Subscribe to alive events for live comment updates from other users.
  // This must be above the `if (!isOpen)` guard so the subscription stays
  // active even when the panel is closed. The hook self-activates only
  // once comments have been loaded into the store.
  useIssueCommentLiveUpdates(aliveChannels)

  const isCompact = useIsDockedPanelCompact()

  if (!isOpen) return null

  if (isPrsDockedSidePanelEnabled && sidePanelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          <CommentPanelHeaderContent
            viewMode={viewMode}
            setViewMode={setViewMode}
            threadPreviewsAuthorList={threadPreviewsAuthorList}
            issueCommentsAuthorList={issueCommentsAuthorList}
            threadPreviewFilterState={threadPreviewFilterState}
            setThreadPreviewFilterState={setThreadPreviewFilterState}
            issueCommentsFilterState={issueCommentsFilterState}
            setIssueCommentsFilterState={setIssueCommentsFilterState}
            iconOnlyReviewCommentsFilter
          />
        </Panel.Header>
        <Panel.Body>
          <CommentsPanelBody
            viewMode={viewMode}
            filteredThreadIds={filteredThreadIds}
            filteredIssueCommentIds={filteredIssueCommentIds}
            conversationItems={conversationItems}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            threadPreviews={threadPreviews}
            totalDiffCommentCount={totalDiffCommentCount}
            limitCount={limitCount}
            isOpen={isOpen}
            onClose={noop}
            tabSize={tabSize}
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
              <CommentsSidePanelFooter
                ref={footerRef}
                commentText={commentText}
                onCommentTextChange={handleCommentTextChange}
                onSubmitComment={handleSubmitComment}
                isPending={isSubmittingComment}
              />
            </FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>,
      sidePanelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderFooter={() => {
        return commentPanelFooter
      }}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
          <CommentPanelHeaderContent
            viewMode={viewMode}
            setViewMode={setViewMode}
            threadPreviewsAuthorList={threadPreviewsAuthorList}
            issueCommentsAuthorList={issueCommentsAuthorList}
            threadPreviewFilterState={threadPreviewFilterState}
            setThreadPreviewFilterState={setThreadPreviewFilterState}
            issueCommentsFilterState={issueCommentsFilterState}
            setIssueCommentsFilterState={setIssueCommentsFilterState}
          />
        </Dialog.Header>
      )}
    >
      <CommentsPanelBody
        viewMode={viewMode}
        limitExceeded={limitExceeded}
        limitCount={limitCount}
        isOpen={isOpen}
        conversationItems={conversationItems}
        onClose={onClose}
        tabSize={tabSize}
        filteredThreadIds={filteredThreadIds}
        filteredIssueCommentIds={filteredIssueCommentIds}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        threadPreviews={threadPreviews}
        totalDiffCommentCount={totalDiffCommentCount}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
      />
    </Dialog>
  )
})
